import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Paper, Grid, Typography, Button, Alert, Tooltip, Chip,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import PublicIcon from '@mui/icons-material/Public';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import {
  getDetectiveSummary, triggerDetectiveRefresh, getDetectiveRefreshJob,
  getDetectiveActiveRefreshJob,
} from '../../../services/detectiveApi';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import {
  PortalLoading, PortalErrorState, PortalEmptyState, PortalSectionCard,
} from '../../../components/portal/PortalStates';
import OperationProgress from './OperationProgress';
import { POLL_INTERVAL_MS } from './pollInterval';
import { pageVariants, sectionVariants } from '../../../animations/variants';
import {
  CK, FONT_BODY, sectionCardSx, statTileSx, statTileFooterSx, tableHeadRowSx,
  headCellSx, rowSx, bodyCellSx, statusChipSx, insetPanelSx, tileEntranceStyle,
} from '../portalStyles';
import { severityColor } from './severity';

// Detective accent, kept identical to engines/detective.js so every Detective
// route reads as one capability. Hardcoded rather than imported for parity with
// the sibling dashboards, which each carry their own engine constant.
const DETECTIVE_COLOR = '#3949AB';
const DETECTIVE_COLOR_DARK = '#283593';

// Detective's exact severity vocabulary — all five are already in severity.js,
// which this task does not modify. UNTRIAGED is Inspector's word and is
// deliberately absent: Detective never emits it.
//
// The empty-string key that SeverityCounts also carries is NOT in this array and
// must never be added to it. This array drives the ordered five-band strip and
// its colour lookup, so an empty key would render an unnamed chip in the middle
// of the ordering. The unrated rows get their own tile, after the strip, in a
// neutral colour.
const SEVERITY_ORDER = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFORMATIONAL'];

// Colours come from severity.js; this map only carries the icon and the hint
// shown on tiles whose label needs explaining.
const severityMeta = {
  CRITICAL: { icon: <ErrorOutlineIcon sx={{ fontSize: 28 }} /> },
  HIGH: { icon: <WarningAmberIcon sx={{ fontSize: 28 }} /> },
  MEDIUM: { icon: <WarningAmberIcon sx={{ fontSize: 28 }} /> },
  LOW: { icon: <InfoOutlinedIcon sx={{ fontSize: 28 }} /> },
  INFORMATIONAL: {
    icon: <InfoOutlinedIcon sx={{ fontSize: 28 }} />,
    hint: 'Reported for awareness — the investigation found nothing that needs action.',
  },
};

const detectiveContainedSx = {
  backgroundColor: DETECTIVE_COLOR,
  fontFamily: FONT_BODY,
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: '6px',
  px: 3,
  '&:hover': { backgroundColor: DETECTIVE_COLOR_DARK },
  '&.Mui-disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' },
};

const captionSx = {
  fontFamily: FONT_BODY,
  fontSize: '0.8rem',
  color: CK.muted,
  display: 'block',
};

// The AWS console entry point for Detective. It is a plain link rather than a
// deep link into one investigation: the empty state is about a graph that holds
// no investigations at all, so there is nothing yet to link to.
const DETECTIVE_CONSOLE_URL = 'https://console.aws.amazon.com/detective/';

// The sentence that turns "no investigations" from a symptom into a fact. It
// appears here, on the findings list and in the docs, because an operator can
// arrive at any of the three first.
const NO_INVESTIGATIONS_EXPLANATION = (accountCount, regionCount) => (
  `Amazon Detective is enrolled and ingesting data across ${accountCount} `
  + `account${accountCount === 1 ? '' : 's'} and ${regionCount} `
  + `region${regionCount === 1 ? '' : 's'}. Detective does not create investigations `
  + 'on its own — it recommends entities worth investigating, and a report exists '
  + 'only once someone runs an investigation. Prism reads those reports; it does '
  + 'not start them. Run an investigation from the Amazon Detective console and it '
  + 'will appear here after the next refresh.'
);

// relativeTime returns a short "2 min ago" style string for a past ISO time.
const relativeTime = (iso) => {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const diffSec = Math.max(0, (Date.now() - then) / 1000);
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.round(diffSec / 60)} min ago`;
  if (diffSec < 86400) return `${Math.round(diffSec / 3600)} h ago`;
  return `${Math.round(diffSec / 86400)} d ago`;
};

const numeric = (v) => Number(v || 0).toLocaleString();

// Detective's entity types are IAM_ROLE and IAM_USER. Title-case them rather than
// printing the raw token, but fall back to the token itself for anything AWS adds
// later — an unknown entity type must still be readable and clickable.
const ENTITY_TYPE_LABELS = {
  IAM_ROLE: 'IAM role',
  IAM_USER: 'IAM user',
};
const entityTypeLabel = (t) => ENTITY_TYPE_LABELS[String(t || '').toUpperCase()] || t || 'Unknown';

// StatTile is the one tile shape both stat rows use, so the coverage row and the
// severity row are exactly the same height and read as one system. onClick is
// optional: a tile with no view to open must not offer a pointer.
function StatTile({ icon, value, color, label, caption, hint, onClick }) {
  const tile = (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        ...statTileSx,
        ...(onClick
          ? {
            cursor: 'pointer',
            transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
            '&:hover': { borderColor: color, boxShadow: `0 4px 12px ${color}20` },
          }
          : {}),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '10px',
            backgroundColor: `${color}12`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Typography
          sx={{
            fontFamily: FONT_BODY,
            fontWeight: 700,
            fontSize: '2.5rem',
            color,
            lineHeight: 1,
            wordBreak: 'break-all',
          }}
        >
          {value}
        </Typography>
      </Box>
      <Box sx={statTileFooterSx}>
        <Typography
          sx={{ fontFamily: FONT_BODY, fontWeight: 600, fontSize: '1rem', color: CK.navy, mb: 0.5 }}
        >
          {label}
        </Typography>
        <Typography sx={{ ...captionSx, fontWeight: 500 }}>{caption}</Typography>
      </Box>
    </Paper>
  );
  // MUI renders the child untouched when title is empty, so tiles without a hint
  // pay nothing for going through Tooltip.
  return <Tooltip title={hint || ''}>{tile}</Tooltip>;
}

// severityRowsFor returns the tile order: Detective's five bands first, then any
// label the data carries that the array does not know about — EXCEPT the empty
// key, which is the unrated bucket and gets its own tile below the strip.
const severityRowsFor = (counts) => [
  ...SEVERITY_ORDER,
  ...Object.keys(counts).filter((s) => s !== '' && !SEVERITY_ORDER.includes(s)).sort(),
];

// DetectiveDashboard renders the investigations view at /securityhub/detective.
//
// It is only ever mounted once DetectiveEntry has read a non-null config, which
// is why there is NO "not configured" branch here: GET /detective/summary rejects
// for an unconfigured tenant, so a rejection at this point is always a real
// failure. Do not try to recover "not configured" by matching the error text —
// portalFetch carries no status code, and the config endpoint already reports
// that state without erroring.
export default function DetectiveDashboard({ config, onConfigChange }) {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshJob, setRefreshJob] = useState(null);
  const pollRef = useRef(null);
  // Consecutive poll failures. GET /refresh/{id} rejects once a job ages out of
  // the table, and retrying that forever leaves a frozen bar and a permanently
  // disabled Refresh button; a few tolerated failures absorb a network blip
  // without turning a dead job into a dead page.
  const pollFailuresRef = useRef(0);
  // The two scalars the poll effect keys on, so a new job object carrying the
  // same id and status does not restart the interval.
  const jobId = refreshJob?.id;
  const jobRunning = refreshJob?.status === 'running';

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getDetectiveSummary();
      setSummary(res?.data || null);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  // On mount, discover a refresh already running server-side (started before a
  // navigation away or a full page reload) and re-attach to it — the poll effect
  // below then takes over exactly as if the user had just clicked Refresh.
  // Without this, anyone who reloads mid-refresh sees an idle page while the
  // fan-out is still running. A null `data` is idle, not an error; a failed
  // lookup is ignored rather than treated as "no job"; and a refresh started in
  // this session wins over whatever the lookup returns.
  useEffect(() => {
    let ignore = false;
    getDetectiveActiveRefreshJob()
      .then((res) => {
        const job = res?.data;
        if (!ignore && job && job.status === 'running') {
          setRefreshJob((prev) => prev || job);
        }
      })
      .catch(() => { /* transient — the user can still refresh manually */ });
    return () => { ignore = true; };
  }, []);

  // Poll the running job; re-fetch the summary once it settles. The counters are
  // the whole point of pressing the button, so this is not a mount-only load.
  //
  // Keyed on the job's ID and a BOOLEAN, never on the job object: every tick
  // stores a fresh object, so depending on it would tear the interval down and
  // rebuild it each cycle — the cadence drifts out to POLL_INTERVAL_MS + RTT and
  // the failure counter resets on every rebuild.
  useEffect(() => {
    if (!jobId || !jobRunning) return undefined;
    pollFailuresRef.current = 0;
    const stop = () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
    pollRef.current = setInterval(async () => {
      try {
        const res = await getDetectiveRefreshJob(jobId);
        const next = res?.data;
        pollFailuresRef.current = 0;
        setRefreshJob(next || null);
        // An empty body settles the job too. It is not a documented outcome —
        // the endpoint rejects for a job it cannot find — but treating it as
        // "still running" would leave the bar up forever with nothing behind it.
        if (!next || next.status !== 'running') {
          stop();
          await loadSummary();
        }
      } catch (_) {
        pollFailuresRef.current += 1;
        if (pollFailuresRef.current >= 3) {
          stop();
          // Drop the job rather than leaving a bar stuck at its last value: the
          // button comes back and the summary shows whatever the run cached.
          setRefreshJob(null);
          loadSummary();
        }
      }
    }, POLL_INTERVAL_MS);
    return stop;
  }, [jobId, jobRunning, loadSummary]);

  const onRefresh = async () => {
    try {
      const res = await triggerDetectiveRefresh();
      setRefreshJob(res?.data || null);
    } catch (e) {
      setError(e.message);
    }
  };

  // Retry re-reads the CONFIG as well as the summary.
  //
  // GET /summary rejects for an unconfigured tenant, so a Detective that was
  // disabled from another tab leaves this page permanently on its error state:
  // every retry of the summary alone rejects again. Detecting that here by
  // matching the error text is exactly what this component must not do —
  // portalFetch carries no status code, and a text match is a contract nothing
  // enforces. Asking the entry gate to re-read the config is the honest
  // recovery: the config endpoint reports "not configured" without erroring, and
  // the gate then renders the setup wizard on its own.
  const onRetry = useCallback(() => {
    if (onConfigChange) onConfigChange();
    loadSummary();
  }, [onConfigChange, loadSummary]);

  const goFindings = (qs) => navigate(`/securityhub/detective/findings${qs ? `?${qs}` : ''}`);

  const refreshing = jobRunning;

  const deploymentLine = (
    <>
      Administrator <b>{summary?.adminAccountId || config?.adminAccountId || '—'}</b>
      {' • '}Home region <b>{summary?.homeRegion || config?.homeRegion || '—'}</b>
    </>
  );

  const header = (subtitle, action) => (
    <PortalPageHeader
      icon={<TravelExploreOutlinedIcon sx={{ fontSize: 24 }} />}
      title="Detective Investigations"
      color={DETECTIVE_COLOR}
      subtitle={subtitle}
      action={action}
    />
  );

  const refreshButton = (label = 'Refresh investigations') => (
    <Tooltip title={refreshing ? 'Refresh already in progress' : 'Fetch the latest investigations from AWS'}>
      <span>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          disabled={refreshing}
          onClick={onRefresh}
          sx={detectiveContainedSx}
        >
          {refreshing ? 'Refreshing…' : label}
        </Button>
      </span>
    </Tooltip>
  );

  // The progress bar is rendered above the empty states as well as above the full
  // page: pressing "Refresh investigations" from an empty state must visibly do
  // something, or the operator presses it again.
  const progressPanel = refreshing ? (
    <motion.div variants={sectionVariants}>
      <Box sx={{ ...insetPanelSx, mb: 3 }}>
        <OperationProgress
          percent={refreshJob?.progress}
          label={refreshJob?.progressLabel || 'Refreshing investigations…'}
          sx={{ mb: 1 }}
        />
        <Typography sx={captionSx}>
          {numeric(refreshJob?.findingsFetched)} investigations fetched so far
          {refreshJob?.regionsFailed > 0 && (
            <> · <b>{refreshJob.regionsFailed}</b> region
              {refreshJob.regionsFailed === 1 ? '' : 's'} could not be read
            </>
          )}
        </Typography>
      </Box>
    </motion.div>
  ) : null;

  // A region that could not be read is the normal partial outcome of the
  // per-region fan-out — one bad region does not fail the run — so a completed
  // job that left gaps must not render identically to a clean one.
  const regionsFailed = refreshJob?.regionsFailed || 0;
  const degraded = refreshJob?.status === 'completed'
    && (regionsFailed > 0 || !!refreshJob?.note);

  // Degraded-state banners, built here so they can render ABOVE the two empty
  // states as well as above the populated page.
  //
  // Do not push these back down next to the tables. The commonest way into
  // config.status === 'partial_failure' is the enable itself partly failing, and
  // at that moment the cache holds zero investigations — so a banner that only
  // rendered on the populated page would be missing exactly when it matters,
  // leaving the operator on "No investigations loaded yet" with nothing pointing
  // at the half-enrolled accounts. Same for a failed or gap-ridden refresh: both
  // explain WHY the page below is empty.
  //
  // The copy is deliberately identical in every state — these exact sentences are
  // quoted in the Detective docs, and a state-dependent variant would be a string
  // the docs do not carry.
  const statusBanners = (
    <>
      {degraded && (
        <motion.div variants={sectionVariants}>
          <Alert
            severity="warning"
            sx={{ mb: 3, borderRadius: '8px', fontFamily: FONT_BODY, '& .MuiAlert-message': { fontFamily: FONT_BODY } }}
          >
            Refresh finished with gaps: {numeric(refreshJob.findingsFetched)} investigations fetched
            {regionsFailed > 0 && <>, {regionsFailed} of {refreshJob.unitsTotal} regions could not be read</>}
            . The counts below cover only what was read.
            {refreshJob.note && <> {refreshJob.note}</>}
          </Alert>
        </motion.div>
      )}

      {refreshJob?.status === 'failed' && (
        <motion.div variants={sectionVariants}>
          <PortalErrorState
            error={refreshJob.errorMessage || 'The investigations refresh failed.'}
            onRetry={onRefresh}
          />
        </motion.div>
      )}

      {/* partial_failure is the terminal status of any partly-successful
          operation — the initial enable, a region update, an add-member run and a
          failed teardown all land here — so the copy describes the state the
          accounts are left in and lets errorMessage name the operation. */}
      {config?.status === 'partial_failure' && (
        <motion.div variants={sectionVariants}>
          <Alert
            severity="warning"
            sx={{ mb: 3, borderRadius: '8px', fontFamily: FONT_BODY, '& .MuiAlert-message': { fontFamily: FONT_BODY } }}
            action={(
              <Button
                size="small"
                onClick={() => navigate('/securityhub/detective/enrollment')}
                sx={{ fontFamily: FONT_BODY, fontWeight: 600, textTransform: 'none', color: 'inherit' }}
              >
                Review
              </Button>
            )}
          >
            Some accounts are not fully enrolled in the behavior graph. {config.errorMessage}
          </Alert>
        </motion.div>
      )}
    </>
  );

  if (loading && !summary) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{header(deploymentLine)}</motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalLoading label="Loading Detective posture…" color={DETECTIVE_COLOR} />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }

  // Case C. A rejection here is a real failure — see the component note. It is
  // checked first so a failed load can never render as a state that invites the
  // operator to act on numbers that were never fetched.
  if (error) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{header(deploymentLine)}</motion.div>
        <motion.div variants={sectionVariants}>
          <PortalErrorState error={error} onRetry={onRetry} />
        </motion.div>
      </motion.div>
    );
  }

  if (!summary) return null;

  // Normalize the keys before use: the tile order and the "unknown label" append
  // both compare strings, and a mixed-case label would otherwise produce a
  // permanently-zero tile plus an invisible duplicate. The empty key normalizes
  // to itself and stays out of the bands.
  const counts = {};
  Object.entries(summary.severityCounts || {}).forEach(([k, v]) => {
    const key = String(k || '').toUpperCase();
    counts[key] = (counts[key] || 0) + Number(v || 0);
  });

  // total is DERIVED. The summary carries no `total` field, and the sum must
  // INCLUDE the unrated bucket — otherwise a tenant whose investigations are
  // merely all unrated is shown the "go and run one in the console" prompt while
  // holding a full table of them.
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  const unrated = counts[''] || 0;
  const severityRows = severityRowsFor(counts);

  const accountCount = Number(summary.accountCount || 0);
  const regionCount = Number(summary.regionCount || 0);
  const graphCount = Number(summary.graphCount || 0);

  // Case A. Prism has never looked — this is a button, not an explanation.
  // everRefreshed is the only field that can tell this apart from case B:
  // lastRefreshAt is null for both, because a refresh that found nothing also
  // leaves the table empty.
  if (!summary.everRefreshed) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{header(deploymentLine, refreshButton())}</motion.div>
        {progressPanel}
        {statusBanners}
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalEmptyState
              icon={<TravelExploreOutlinedIcon sx={{ fontSize: 28 }} />}
              title="No investigations loaded yet"
              description="Prism has not read your behavior graph yet. Refresh to pull the investigations Amazon Detective holds."
              color={DETECTIVE_COLOR}
              action={(
                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  disabled={refreshing}
                  onClick={onRefresh}
                  sx={detectiveContainedSx}
                >
                  {refreshing ? 'Refreshing…' : 'Refresh investigations'}
                </Button>
              )}
            />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }

  // Case B. Prism looked and there is nothing to find — and there never will be
  // unless somebody starts an investigation. This is Detective's EXPECTED steady
  // state, so it gets an explanation rather than a bare "no results": Detective
  // creates no investigation records on its own, and Prism deliberately never
  // calls StartInvestigation. "No investigations found" alone would be true and
  // useless, and the operator would reasonably conclude Prism is broken.
  if (total === 0) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{header(deploymentLine, refreshButton())}</motion.div>
        {progressPanel}
        {statusBanners}
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalEmptyState
              icon={<TravelExploreOutlinedIcon sx={{ fontSize: 28 }} />}
              title="No investigations yet"
              description={NO_INVESTIGATIONS_EXPLANATION(accountCount, regionCount)}
              color={DETECTIVE_COLOR}
              action={(
                <Button
                  variant="contained"
                  href={DETECTIVE_CONSOLE_URL}
                  target="_blank"
                  rel="noreferrer"
                  sx={detectiveContainedSx}
                >
                  Open the Detective console
                </Button>
              )}
            />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }

  // LastRefreshAt is a nullable pointer — the backend sends null rather than a
  // zero time precisely so this does not render as "January year 1".
  const lastRefreshText = summary.lastRefreshAt
    ? (relativeTime(summary.lastRefreshAt) || 'unknown')
    : 'never';
  const perEntityType = summary.perEntityType || [];
  const perAccount = summary.perAccount || [];

  // Accounts and regions lead the page. The investigation count is legitimately
  // 0 on a healthy graph, so a 0 at the top would state the opposite of what is
  // true.
  //
  // graphCount sits beside regionCount because the two diverge exactly when a
  // region was enabled but its CreateGraph never landed — surfacing that
  // discrepancy is what makes the orphan sweep's work visible.
  const coverageCards = [
    {
      key: 'accounts',
      label: 'Accounts covered',
      value: numeric(accountCount),
      color: CK.blue,
      icon: <AccountTreeIcon sx={{ fontSize: 28 }} />,
      caption: 'enrolled in the behavior graph',
      onClick: () => navigate('/securityhub/detective/enrollment'),
    },
    {
      key: 'regions',
      label: 'Regions covered',
      value: numeric(regionCount),
      color: CK.purple,
      icon: <PublicIcon sx={{ fontSize: 28 }} />,
      caption: 'regions ingesting data',
      onClick: () => navigate('/securityhub/detective/settings'),
    },
    {
      key: 'graphs',
      label: 'Behavior graphs',
      value: numeric(graphCount),
      color: graphCount === regionCount ? CK.green : CK.amber,
      icon: <HubOutlinedIcon sx={{ fontSize: 28 }} />,
      caption: graphCount === regionCount
        ? 'one graph per covered region'
        : 'fewer graphs than regions — a region has no graph',
      hint: graphCount === regionCount
        ? ''
        : 'Amazon Detective builds one behavior graph per region. A region without one is not ingesting anything; re-run the region update from Settings to build the missing graph.',
      onClick: () => navigate('/securityhub/detective/settings'),
    },
  ];

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      {/* === Header === */}
      <motion.div variants={sectionVariants}>
        <PortalPageHeader
          icon={<TravelExploreOutlinedIcon sx={{ fontSize: 24 }} />}
          title="Detective Investigations"
          color={DETECTIVE_COLOR}
          subtitle={(
            <>
              {deploymentLine}
              {' • '}<b>{accountCount}</b> account{accountCount === 1 ? '' : 's'} across{' '}
              <b>{regionCount}</b> region{regionCount === 1 ? '' : 's'}
              {' • '}Last refresh: <b>{lastRefreshText}</b>
            </>
          )}
          action={refreshButton()}
        />
      </motion.div>

      {progressPanel}

      {statusBanners}

      {/* === Coverage stat row — the headline, ahead of the severity strip ===
          No motion wrapper: the tiles animate themselves in pure CSS, and a
          motion component here can write an inline opacity onto them once its own
          animation settles. */}
      <Grid container spacing={3} sx={{ mb: 2 }}>
        {coverageCards.map((c, i) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={c.key}>
            <div style={tileEntranceStyle(i)}>
              <StatTile
                icon={c.icon}
                value={c.value}
                color={c.color}
                label={c.label}
                caption={c.caption}
                hint={c.hint}
                onClick={c.onClick}
              />
            </div>
          </Grid>
        ))}
      </Grid>

      <motion.div variants={sectionVariants}>
        <Typography sx={{ ...captionSx, mb: 3 }}>
          <b>{numeric(total)}</b> investigation{total === 1 ? '' : 's'} cached
          {' · '}<b>{numeric(summary.activeCount)}</b> active
          {' · '}<b>{numeric(summary.archivedCount)}</b> archived
        </Typography>
      </motion.div>

      {/* === Severity strip === */}
      <Grid container spacing={3} sx={{ mb: 2 }}>
        {severityRows.map((sev, i) => {
          const n = counts[sev] || 0;
          const meta = severityMeta[sev] || {};
          const color = severityColor(sev);
          return (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={sev}>
              <div style={tileEntranceStyle(i)}>
                <StatTile
                  icon={meta.icon || <InfoOutlinedIcon sx={{ fontSize: 28 }} />}
                  value={n.toLocaleString()}
                  color={color}
                  label={sev.charAt(0) + sev.slice(1).toLowerCase()}
                  caption={n === 1 ? 'investigation' : 'investigations'}
                  hint={meta.hint}
                  onClick={() => goFindings(`severity=${encodeURIComponent(sev)}`)}
                />
              </div>
            </Grid>
          );
        })}

        {/* The sixth tile is not decoration, and it is rendered ONLY when the
            count is non-zero. Without it the five bands sum to less than the
            investigation count with nothing explaining the gap — which is the
            shape that gets reported as data loss rather than as a known
            limitation. A tenant whose rows all carry a severity never sees it,
            and it disappears on its own if AWS ever starts returning Severity on
            the list path.

            It sits AFTER the strip in a neutral colour, and '' is deliberately
            not a member of SEVERITY_ORDER — an empty key there would render an
            unnamed chip in the middle of the band ordering. */}
        {unrated > 0 && (
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <div style={tileEntranceStyle(severityRows.length)}>
              <StatTile
                icon={<HelpOutlineIcon sx={{ fontSize: 28 }} />}
                value={unrated.toLocaleString()}
                color={CK.muted}
                label="Unrated"
                caption={unrated === 1 ? 'investigation' : 'investigations'}
                hint="Amazon Detective returns a severity with the full investigation report, not in the list. Prism fills it in when the investigation is opened."
                onClick={() => goFindings('severity=UNRATED')}
              />
            </div>
          </Grid>
        )}
      </Grid>

      {unrated > 0 && (
        <motion.div variants={sectionVariants}>
          <Typography sx={{ ...captionSx, mb: 3 }}>
            <b>{numeric(unrated)}</b> investigation{unrated === 1 ? ' is' : 's are'} not yet
            rated. Amazon Detective returns a severity with the full investigation report,
            not in the list — Prism fills it in when the investigation is opened.
          </Typography>
        </motion.div>
      )}

      {/* === Per-entity-type breakdown === */}
      <motion.div variants={sectionVariants}>
        <PortalSectionCard
          icon={<BadgeOutlinedIcon sx={{ fontSize: 20 }} />}
          title="Investigations by entity type"
          subtitle="Detective investigates IAM principals — select a type to open the matching investigations"
          color={DETECTIVE_COLOR}
          action={(
            <Chip size="small" label={`${perEntityType.length} types`} sx={statusChipSx('info')} />
          )}
          sx={{ mb: 3 }}
        >
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={tableHeadRowSx}>
                  <TableCell sx={headCellSx}>Entity type</TableCell>
                  <TableCell align="right" sx={{ ...headCellSx, width: 160 }}>Investigations</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {perEntityType.map((row, index) => (
                  <TableRow
                    key={row.entityType}
                    hover
                    sx={{ ...rowSx(index), cursor: 'pointer' }}
                    onClick={() => goFindings(`type=${encodeURIComponent(row.entityType)}`)}
                  >
                    <TableCell sx={{ ...bodyCellSx, wordBreak: 'break-word' }}>
                      {entityTypeLabel(row.entityType)}
                    </TableCell>
                    <TableCell align="right" sx={bodyCellSx}>
                      <Chip
                        label={numeric(row.count)}
                        size="small"
                        sx={{ ...statusChipSx(row.count > 0 ? 'warning' : 'neutral'), minWidth: 70 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {perEntityType.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={2} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                      <PortalEmptyState
                        icon={<BadgeOutlinedIcon sx={{ fontSize: 28 }} />}
                        title="No entity types recorded yet"
                        description="Every investigation names the IAM role or user it examined. Types appear here as soon as the cache holds an investigation."
                        color={DETECTIVE_COLOR}
                        py={2}
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </PortalSectionCard>
      </motion.div>

      {/* === Per-account table === */}
      <motion.div variants={sectionVariants}>
        <PortalSectionCard
          icon={<AccountTreeIcon sx={{ fontSize: 20 }} />}
          title="Investigations by account"
          subtitle="Select an account to open the matching investigations"
          color={DETECTIVE_COLOR}
          action={(
            <Chip
              size="small"
              label={`${perAccount.length} accounts with investigations`}
              sx={statusChipSx('info')}
            />
          )}
        >
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={tableHeadRowSx}>
                  <TableCell sx={headCellSx}>Account</TableCell>
                  <TableCell align="right" sx={{ ...headCellSx, width: 180 }}>Investigations</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {perAccount.map((row, index) => (
                  <TableRow
                    key={row.accountId}
                    hover
                    sx={{ ...rowSx(index), cursor: 'pointer' }}
                    onClick={() => goFindings(`account=${encodeURIComponent(row.accountId)}`)}
                  >
                    <TableCell sx={bodyCellSx}>{row.accountId}</TableCell>
                    <TableCell align="right" sx={bodyCellSx}>
                      <Chip
                        icon={<WarningAmberIcon />}
                        label={numeric(row.count)}
                        size="small"
                        sx={{ ...statusChipSx(row.count > 0 ? 'warning' : 'neutral'), minWidth: 80 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {perAccount.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={2} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                      <PortalEmptyState
                        icon={<AccountTreeIcon sx={{ fontSize: 28 }} />}
                        title="No accounts with investigations"
                        description="Enrolled accounts appear here as soon as an investigation names one of their IAM principals."
                        color={DETECTIVE_COLOR}
                        py={2}
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </PortalSectionCard>
      </motion.div>
    </motion.div>
  );
}
